/**
 * TabSessionRegistry — single source of truth for the tab ↔ engine-session
 * mapping (P2B #3).
 *
 * Why a module-level singleton instead of Zustand state?
 *
 *   The mapping is read & written from two separate Zustand stores
 *   (`useEditorStore` for tab lifecycle, `usePluginExecutionStore` for
 *   session lifecycle).  Putting it inside either store leaks ownership and
 *   has historically allowed one side to update without the other (see
 *   memories: "session routing", "session sync"; orphan reverse-mappings
 *   when a tab was relinked to a new session, etc).
 *
 *   The registry exposes *atomic* link/unlink operations that always keep
 *   the forward (tabId → sessionId) and reverse (sessionId → tabId)
 *   indexes consistent, and emits a single change notification per mutation
 *   so the execution store can publish a derived snapshot for reactive
 *   consumers.
 */

export interface TabSessionLink {
    tabId: string;
    sessionId: string;
    filePath?: string;
}

type ChangeListener = () => void;

export class TabSessionRegistry {
    /** Forward index — tabId → full link. */
    private readonly byTab = new Map<string, TabSessionLink>();
    /** Reverse index — sessionId → tabId. */
    private readonly bySession = new Map<string, string>();
    private readonly listeners = new Set<ChangeListener>();

    /**
     * Atomically link a tab to a session.
     *
     * Replaces any prior link for the same `tabId` AND any prior link for
     * the same `sessionId` (orphan-free).  When `filePath` is omitted the
     * existing filePath (if any) is preserved.
     */
    link(link: TabSessionLink): void {
        const { tabId, sessionId } = link;
        // Drop a stale tab→session if this tab pointed at a different session.
        const priorByTab = this.byTab.get(tabId);
        if (priorByTab && priorByTab.sessionId !== sessionId) {
            this.bySession.delete(priorByTab.sessionId);
        }
        // Drop a stale session→tab if this session was linked to a different tab.
        const priorTabForSession = this.bySession.get(sessionId);
        if (priorTabForSession && priorTabForSession !== tabId) {
            this.byTab.delete(priorTabForSession);
        }
        const filePath =
            "filePath" in link ? link.filePath : priorByTab?.filePath;
        const next: TabSessionLink = filePath === undefined
            ? { tabId, sessionId }
            : { tabId, sessionId, filePath };
        this.byTab.set(tabId, next);
        this.bySession.set(sessionId, tabId);
        this.notify();
    }

    /** Atomically unlink by tab. Returns the prior link if any. */
    unlinkTab(tabId: string): TabSessionLink | undefined {
        const prior = this.byTab.get(tabId);
        if (!prior) return undefined;
        this.byTab.delete(tabId);
        // Only delete the reverse index if it still points at this tab —
        // a relink may have repointed it already.
        if (this.bySession.get(prior.sessionId) === tabId) {
            this.bySession.delete(prior.sessionId);
        }
        this.notify();
        return prior;
    }

    /** Atomically unlink by session. Returns the prior link if any. */
    unlinkSession(sessionId: string): TabSessionLink | undefined {
        const tabId = this.bySession.get(sessionId);
        if (!tabId) return undefined;
        const prior = this.byTab.get(tabId);
        this.bySession.delete(sessionId);
        if (prior && prior.sessionId === sessionId) {
            this.byTab.delete(tabId);
        }
        this.notify();
        return prior;
    }

    getSessionForTab(tabId: string): string | undefined {
        return this.byTab.get(tabId)?.sessionId;
    }

    getTabForSession(sessionId: string): string | undefined {
        return this.bySession.get(sessionId);
    }

    getFilePathForTab(tabId: string): string | undefined {
        return this.byTab.get(tabId)?.filePath;
    }

    /**
     * Atomically update a link's filePath (rename / Save As).  No-op if the
     * tab is not linked.  Preserves the session/tab mapping.
     */
    updateFilePath(tabId: string, filePath: string | undefined): void {
        const prior = this.byTab.get(tabId);
        if (!prior) return;
        if (prior.filePath === filePath) return;
        const next: TabSessionLink = filePath === undefined
            ? { tabId: prior.tabId, sessionId: prior.sessionId }
            : { tabId: prior.tabId, sessionId: prior.sessionId, filePath };
        this.byTab.set(tabId, next);
        this.notify();
    }

    /** Iterate all current links (cloned objects — safe to retain). */
    *entries(): IterableIterator<TabSessionLink> {
        for (const link of this.byTab.values()) {
            yield { ...link };
        }
    }

    /** Number of active links. */
    get size(): number {
        return this.byTab.size;
    }

    clear(): void {
        if (this.byTab.size === 0 && this.bySession.size === 0) return;
        this.byTab.clear();
        this.bySession.clear();
        this.notify();
    }

    /**
     * Subscribe to mutation events.  Listeners fire after every link /
     * unlink / updateFilePath / clear that actually changed state.  Returns
     * an unsubscribe function.
     */
    subscribe(listener: ChangeListener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Snapshot the current mapping as a plain `Map<tabId, sessionId>`.
     *
     * Used by `usePluginExecutionStore` to derive its `tabSessionMap`
     * field for reactive selectors that still observe it via Zustand.
     */
    snapshotTabSessionMap(): Map<string, string> {
        const out = new Map<string, string>();
        for (const link of this.byTab.values()) {
            out.set(link.tabId, link.sessionId);
        }
        return out;
    }

    private notify(): void {
        for (const fn of this.listeners) fn();
    }
}

/** Process-wide singleton used by the editor + plugin-execution stores. */
export const tabSessionRegistry = new TabSessionRegistry();
